'use client';
import { Tabs } from '@/components/atoms/Tabs/Tabs';
import { Section } from '@/components/atoms/Section';
import LinkFactory, { generateStockLinkBySymbol } from '@/utils/links';
import { QuotesCodeMapping } from '@/constants/quote/quote';
import { colorColumn, timeColumn } from '@/components/atoms/ReactTable/columns';
import { saveColumn } from '@/components/atoms/ReactTable/columns';
import { useQuery } from '@tanstack/react-query';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import Skeleton from './Skeleton';
import useGetUserSave from '@/components/atoms/ReactTable/hooks/useGetUserSave';
import { getGlobalList } from './getGlobalList';
import ReactTable from '@/components/atoms/ReactTable';
import { TABS } from './config';

type RowFormat = {
  [QuotesCodeMapping.SYMBOL]: string;
  [QuotesCodeMapping.TRADE_TIME]: number;
  [QuotesCodeMapping.CHINESE_NAME]: string;
  [QuotesCodeMapping.ENGLISH_NAME]: string;
  [QuotesCodeMapping.BID]: string;
  [QuotesCodeMapping.ASK]: string;
  [QuotesCodeMapping.CHANGE_PERCENT_V2]: string;
  [QuotesCodeMapping.CHANGE_V2]: string;
};

const basicColumns = [
  timeColumn,
  {
    header: () => <div className='text-left'>名稱</div>,
    accessorKey: QuotesCodeMapping.CHINESE_NAME.toString(),
    cell: (props: CellContext<RowFormat, string>) => {
      const symbol = props?.row?.original?.[QuotesCodeMapping.SYMBOL];
      const link = generateStockLinkBySymbol(symbol);
      const value = props.getValue();

      return (
        <a className='line-clamp-2 w-full pr-2 text-left' href={link} title={value}>
          {value}
        </a>
      );
    },
    enableSorting: false
  },
  colorColumn(QuotesCodeMapping.CLOSE.toString(), '最新價', {
    referenceColumn: QuotesCodeMapping.CHANGE_PERCENT_V2.toString(),
    enableSorting: false
  }),
  colorColumn(QuotesCodeMapping.CHANGE_V2.toString(), '變動', {
    referenceColumn: QuotesCodeMapping.CHANGE_PERCENT_V2.toString(),
    enableSorting: false
  }),
  colorColumn(QuotesCodeMapping.CHANGE_PERCENT_V2.toString(), '變%', { isPercentage: true, enableSorting: false })
];

const List = ({ tab, userSaveList }: { tab: string; userSaveList: string[] }) => {
  const { data } = useQuery({
    queryKey: ['global-list', tab],
    queryFn: getGlobalList,
    select: res => res?.data?.items || [],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    placeholderData: prev => prev
  });

  const columns = [saveColumn(userSaveList), ...basicColumns];

  return (
    <div className='mt-2 px-3 lg:px-0'>
      <ReactTable data={data} columns={columns} enablePagination Skeleton={<Skeleton />} />
    </div>
  );
};

const GlobalList = () => {
  const [tab, setTab] = useState(TABS[0].value);
  const readMore = `${LinkFactory.wwwChannelLink}/stockQ/Risk`;

  const onClickTab = (index: number) => {
    setTab(TABS[index].value);
  };

  // 取得使用者自選列表
  const { data: userSaveList } = useGetUserSave();

  return (
    <div className='w-full lg:max-w-[464px]'>
      <Section title='看看國際' readMore={readMore} />
      <div className='mt-4 bg-white pb-4 lg:pl-5'>
        <Tabs tabs={TABS} onClick={onClickTab} onHover={onClickTab} />
        <List tab={tab} userSaveList={userSaveList} />
      </div>
    </div>
  );
};

export default GlobalList;
